import type { FollowUp } from '@floorpoll/shared';

/**
 * The follow-ups a phone has collected, kept so they survive the end of the
 * poll. While the poll is live the server is the source of truth — GET
 * /public/join/{code} replays them — but the join code stops resolving the
 * moment the presentation ends, and that is the case this cache exists for.
 *
 * Keyed by join code: a taker is never told the quizId.
 */
const KEY = 'floorpoll.followUps';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const MAX_POLLS = 5;

/** One option this device picked, and the reply it earned. */
export interface RecapPick {
  optionText: string;
  followUp: FollowUp;
}

export interface RecapItem {
  questionId: string;
  questionText: string;
  /** One entry per option picked — several only on a multi-select question. */
  picks: RecapPick[];
}

interface Entry {
  pollTitle: string;
  updatedAt: number;
  items: RecapItem[];
}

type Cache = Record<string, Entry>;

/**
 * A row written before questions could take several answers, which stored the
 * one pick inline instead of a `picks` list.
 */
interface LegacyRecapItem {
  questionId: string;
  questionText: string;
  optionText?: string;
  followUp?: FollowUp;
}

/**
 * Upgrades a stored row to the current shape.
 *
 * This cache outlives a deploy — that is its whole job, surviving the end of a
 * poll — so a phone that answered last week arrives holding the old shape. It
 * is migrated on read rather than dropped, because dropping it would quietly
 * empty the recap of exactly the polls it exists to remember.
 */
function normalizeItem(raw: RecapItem | LegacyRecapItem): RecapItem {
  const legacy = raw as LegacyRecapItem;
  const picks = Array.isArray((raw as RecapItem).picks)
    ? (raw as RecapItem).picks
    : legacy.followUp
      ? [{ optionText: legacy.optionText ?? '', followUp: legacy.followUp }]
      : [];
  return { questionId: raw.questionId, questionText: raw.questionText, picks };
}

function read(): Cache {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};

    return Object.fromEntries(
      Object.entries(parsed as Record<string, Entry>).map(([code, entry]) => [
        code,
        { ...entry, items: (entry.items ?? []).map(normalizeItem) },
      ]),
    );
  } catch {
    // Corrupt or unavailable storage must not break the taker view.
    return {};
  }
}

function write(cache: Cache): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(cache));
  } catch {
    // A full or blocked store costs the recap, nothing else.
  }
}

/** Drops stale polls, then the oldest beyond the cap. */
function prune(cache: Cache, now: number): Cache {
  const fresh = Object.entries(cache).filter(([, entry]) => now - entry.updatedAt < MAX_AGE_MS);
  const kept = fresh.sort((a, b) => b[1].updatedAt - a[1].updatedAt).slice(0, MAX_POLLS);
  return Object.fromEntries(kept);
}

export function readRecap(code: string): RecapItem[] {
  return prune(read(), Date.now())[code]?.items ?? [];
}

/** Merges by questionId, so a re-delivered follow-up updates rather than doubles. */
export function rememberFollowUps(code: string, pollTitle: string, items: RecapItem[]): void {
  // Not a redundant early return: Join calls this on every resync, so an
  // empty write here would burn a slot under the 5-poll cap and evict a real
  // recap.
  if (items.length === 0) return;
  const now = Date.now();
  const cache = prune(read(), now);
  const existing = cache[code]?.items ?? [];
  const merged = new Map(existing.map((entry) => [entry.questionId, entry]));
  for (const entry of items) merged.set(entry.questionId, entry);

  const next = { ...cache, [code]: { pollTitle, updatedAt: now, items: [...merged.values()] } };
  write(prune(next, now));
}

/**
 * Aligns the cache with what the server says this device has answered, and
 * returns what survived. Resetting a poll's results clears the vote lock, so
 * without this the phone would show a recap for votes that no longer exist.
 */
export function reconcileRecap(code: string, answeredQuestionIds: string[]): RecapItem[] {
  const now = Date.now();
  const cache = prune(read(), now);
  const entry = cache[code];
  if (!entry) return [];

  const answered = new Set(answeredQuestionIds);
  const items = entry.items.filter((candidate) => answered.has(candidate.questionId));
  write({ ...cache, [code]: { ...entry, items } });
  return items;
}
